import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import {
  distinctUntilChanged,
  map,
  skip,
  takeUntil,
} from 'rxjs/operators';
import {
  MenuConfigService,
  HomepageSection,
} from '@app/core/services/menu-config.service';
import { PermissionService } from '@app/core/services/permission.service';
import { AuthService } from '@app/core/services/auth.service';
import { UserSettingsService } from '@app/core/services/user-settings.service';
import { CommerceModulesService } from '@app/core/commerce/commerce-modules.service';

export interface TenantWorkspaceSnapshot {
  tenantId: string;
  tenantName: string;
  flagshipStore: string;
  primaryWarehouse: string;
  activeProductsCount: number;
  activeOrdersSummary: string;
  priceBookCode: string;
}

const TENANT_WORKSPACE_SNAPSHOTS: Record<string, TenantWorkspaceSnapshot> = {
  '1': {
    tenantId: '1',
    tenantName: '#1 UniRetail Default Workspace',
    flagshipStore: 'UniRetail Flagship Store (ID #1)',
    primaryWarehouse: 'UniRetail Central Warehouse',
    activeProductsCount: 120,
    activeOrdersSummary: 'UniRetail Omni-Channel Orders',
    priceBookCode: 'UNIRETAIL-STD-VND',
  },
  '2': {
    tenantId: '2',
    tenantName: '#2 KidsPlaza Mother & Baby Workspace',
    flagshipStore: 'KidsPlaza District 1 & Cau Giay (ID #2001, #2002)',
    primaryWarehouse: 'KP-WH-HCM & KP-WH-HN',
    activeProductsCount: 4,
    activeOrdersSummary: 'KP-ORD-20261001, KP-ORD-20261002 (Meiji / Aptamil / Bobby)',
    priceBookCode: 'KP-RETAIL-VND',
  },
  '3': {
    tenantId: '3',
    tenantName: '#3 Franchise Specialty Coffee & Bakery Workspace',
    flagshipStore: 'Franchise Flagship Da Nang & Can Tho (ID #3001, #3002)',
    primaryWarehouse: 'FR-WH-DN & FR-WH-CT',
    activeProductsCount: 4,
    activeOrdersSummary: 'FR-ORD-20261001, FR-ORD-20261002 (Dalat Arabica / Uji Matcha)',
    priceBookCode: 'FR-STORE-VND',
  },
};

@Component({
  selector: 'app-homepage',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './homepage.component.html',
  styleUrls: ['./homepage.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomepageComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly http = inject(HttpClient);
  private readonly menuConfigService = inject(MenuConfigService);
  private readonly permissionService = inject(PermissionService);
  private readonly authService = inject(AuthService, { optional: true });
  private readonly userSettingsService = inject(UserSettingsService);
  private readonly commerceModules = inject(CommerceModulesService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  // Dashboard sections
  businessCenterItems = signal<HomepageSection[]>([]);
  activeTenantSnapshot = signal<TenantWorkspaceSnapshot>(TENANT_WORKSPACE_SNAPSHOTS['1']);

  // Show more functionality
  showMoreDropdown = signal<string | null>(null);
  dropdownPositions = signal<Map<string, 'bottom' | 'top'>>(new Map());

  private static readonly SECTION_VISUALS: Record<
    string,
    { icon: string; badgeClass: string }
  > = {
    Channel: {
      icon: 'storefront',
      badgeClass: 'badge-blue',
    },
    POS: {
      icon: 'point_of_sale',
      badgeClass: 'badge-teal',
    },
    Product: {
      icon: 'hexagon',
      badgeClass: 'badge-purple',
    },
    'Price book': {
      icon: 'sell',
      badgeClass: 'badge-indigo',
    },
    Inventory: {
      icon: 'inventory_2',
      badgeClass: 'badge-amber',
    },
    Procurement: {
      icon: 'shopping_cart',
      badgeClass: 'badge-rose',
    },
    Distribution: {
      icon: 'storefront',
      badgeClass: 'badge-cyan',
    },
    'Third-Party': {
      icon: 'local_mall',
      badgeClass: 'badge-violet',
    },
    Orders: {
      icon: 'receipt_long',
      badgeClass: 'badge-emerald',
    },
    'O2O Orders': {
      icon: 'description',
      badgeClass: 'badge-sky',
    },
    Warehouse: {
      icon: 'warehouse',
      badgeClass: 'badge-orange',
    },
    'E-Invoice': {
      icon: 'receipt_long',
      badgeClass: 'badge-pink',
    },
    'After-Sales': {
      icon: 'headset_mic',
      badgeClass: 'badge-blue-light',
    },
    Marketing: {
      icon: 'campaign',
      badgeClass: 'badge-green',
    },
    Finance: {
      icon: 'account_balance',
      badgeClass: 'badge-amber',
    },
    Report: {
      icon: 'assessment',
      badgeClass: 'badge-indigo',
    },
    commerce_crm: {
      icon: 'handshake',
      badgeClass: 'badge-violet',
    },
    commerce_member: {
      icon: 'loyalty',
      badgeClass: 'badge-pink',
    },
    commerce_hrm: {
      icon: 'badge',
      badgeClass: 'badge-sky',
    },
    commerce_bpm: {
      icon: 'account_tree',
      badgeClass: 'badge-emerald',
    },
    commerce_platform: {
      icon: 'domain',
      badgeClass: 'badge-blue',
    },
  };

  private static readonly FALLBACK_BADGES = [
    'badge-blue',
    'badge-teal',
    'badge-purple',
    'badge-amber',
    'badge-rose',
    'badge-violet',
    'badge-emerald',
    'badge-sky',
    'badge-orange',
    'badge-pink',
    'badge-blue-light',
    'badge-green',
  ];

  constructor() {}

  ngOnInit(): void {
    // Translations are already loaded by LanguageInitService during app initialization
    // Skip loading to avoid duplicate API calls - just initialize menu items
    // If translations are needed, they will be loaded by LanguageInitService
    this.initializeMenuItems();
    this.refreshTenantSnapshot(this.authService?.getTenantId() || '1');

    this.authService?.tenantSwitched$
      ?.pipe(takeUntil(this.destroy$))
      .subscribe((tenantId) => {
        this.refreshTenantSnapshot(tenantId || '1');
        this.commerceModules.clearCache();
        this.commerceModules
          .loadModules()
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.initializeMenuItems();
            this.cdr.detectChanges();
          });
        this.initializeMenuItems();
        this.cdr.detectChanges();
      });

    // Subscribe to permission changes to refresh menu when permissions are loaded
    this.permissionService.userPermissions$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // Re-initialize menu items when permissions change
        this.initializeMenuItems();
        // Force change detection to ensure UI updates
        this.cdr.detectChanges();
      });

    // Also subscribe to permissions loaded state
    this.permissionService.permissionsLoaded$
      .pipe(takeUntil(this.destroy$))
      .subscribe((loaded) => {
        if (loaded) {
          // Re-initialize menu items when permissions are loaded
          this.initializeMenuItems();
          // Force change detection to ensure UI updates
          this.cdr.detectChanges();
        }
      });

    // Commerce tiles depend on /api/bo/modules, which may resolve after permissions
    this.commerceModules
      .loadModules()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.initializeMenuItems();
        this.cdr.detectChanges();
      });

    // Listen for language changes
    this.translateService.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.initializeMenuItems();
      });

    // Refresh when the "show unavailable menu items" preference changes
    this.userSettingsService.userPreferences$
      .pipe(
        map((prefs) => prefs.showUnavailableMenuItems),
        distinctUntilChanged(),
        skip(1),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.initializeMenuItems();
        this.cdr.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private refreshTenantSnapshot(tenantId: string): void {
    const normalized = (tenantId || '1').trim();
    const preset = TENANT_WORKSPACE_SNAPSHOTS[normalized];
    if (preset) {
      this.activeTenantSnapshot.set(preset);
      return;
    }
    this.activeTenantSnapshot.set({
      tenantId: normalized,
      tenantName: `#${normalized} Custom Retail Tenant Workspace`,
      flagshipStore: `T${normalized}-STORE-01 Flagship Store`,
      primaryWarehouse: `T${normalized}-WH-01 Central Warehouse`,
      activeProductsCount: 0,
      activeOrdersSummary: `Isolated Workspace for Tenant #${normalized}`,
      priceBookCode: `T${normalized}-STD-VND`,
    });
  }

  private initializeMenuItems(): void {
    // Get menu configuration from shared service
    const menuConfig = this.menuConfigService.getHomepageMenuConfig();
    // Apply translations to titles and labels
    const translatedConfig = menuConfig.map((section) => ({
      title: this.translateService.instant(section.title),
      name: section.name,
      icon: section.icon,
      items: section.items.map((item) => ({
        name: item.name, // Preserve unique name for tracking
        label: this.translateService.instant(item.label),
        route: item.route,
        hasRoute: item.hasRoute,
      })),
    }));
    this.businessCenterItems.set(translatedConfig);
  }

  getSectionIcon(section: HomepageSection): string {
    return (
      HomepageComponent.SECTION_VISUALS[section.name]?.icon ||
      section.icon ||
      'grid_view'
    );
  }

  getSectionBadgeClass(section: HomepageSection, index: number = 0): string {
    return (
      HomepageComponent.SECTION_VISUALS[section.name]?.badgeClass ||
      HomepageComponent.FALLBACK_BADGES[
        index % HomepageComponent.FALLBACK_BADGES.length
      ]
    );
  }

  navigateToRoute(route: string): void {
    if (route) {
      this.router.navigate([route]);
    }
  }

  getVisibleItems(items: any[]): any[] {
    // For sections with more than 7 items, show only 6 items
    // For other sections, show all items
    return items.length > 7 ? items.slice(0, 6) : items;
  }

  getHiddenItems(items: any[]): any[] {
    // Only return hidden items for sections with more than 7 items
    return items.length > 7 ? items.slice(6) : [];
  }

  hasMoreItems(items: any[]): boolean {
    // Only show "Show more" for sections with more than 7 items
    return items.length > 7;
  }

  toggleMoreDropdown(sectionTitle: string, event?: Event): void {
    const currentDropdown = this.showMoreDropdown();
    if (currentDropdown === sectionTitle) {
      this.showMoreDropdown.set(null);
    } else {
      this.showMoreDropdown.set(sectionTitle);

      // Calculate dropdown position based on available space
      if (event) {
        this.calculateDropdownPosition(sectionTitle, event);
      }
    }
  }

  private calculateDropdownPosition(sectionTitle: string, event: Event): void {
    const button = event.currentTarget as HTMLElement;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownMaxHeight = 320; // max-h-80 = 20rem = 320px
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    // If there's not enough space below but enough space above, open upward
    const shouldOpenUpward =
      spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow;

    const positions = new Map(this.dropdownPositions());
    positions.set(sectionTitle, shouldOpenUpward ? 'top' : 'bottom');
    this.dropdownPositions.set(positions);
  }

  getDropdownPosition(sectionTitle: string): 'bottom' | 'top' {
    return this.dropdownPositions().get(sectionTitle) || 'bottom';
  }

  isDropdownOpen(sectionTitle: string): boolean {
    return this.showMoreDropdown() === sectionTitle;
  }

  closeDropdown(): void {
    this.showMoreDropdown.set(null);
  }

  toTestId(value: unknown): string {
    return String(value ?? '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }
}
